// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O DIÁRIO — «tudo o que aconteceu», uma frase por gesto
// ============================================================================
//
// 📗 §5.5 do desenho: «a peça mais barata E a que torna as outras utilizáveis — ninguém pede
//    para desfazer o que não vê».
//
// ⚠️⚠️ **AS QUATRO EXIGÊNCIAS VIVEM NO SERVIDOR (`apps/api/src/diario.ts`) — este ecrã só as
//    MOSTRA.** A frase por acção, a distinção do sistema e o agrupamento por gesto são todos lá,
//    com o guarda que recusa uma acção sem frase. Repeti-los aqui seria a segunda formulação que
//    a regra 8 do projecto proíbe.
//
// ⚠️⚠️ **O `alvo` É A EXIGÊNCIA 3 DO §5.6 — «tudo o que aconteceu a ESTA VIAGEM».** Ligado a
//    2026-09-15: o `Mapa.tsx` abre-o do painel de detalhe e do menu de contexto da célula, com
//    a viatura, os dias da corrida, a referência e o pedido. 📌 **O que cumpre a exigência não
//    é o botão, é o filtro**: um «Diário» na célula que abrisse os ~1 400 registos do plano
//    cumpria a letra e falhava a pergunta. Quem decide o que pertence à viagem é o servidor
//    (`apps/api/src/diario.ts`, §5) — aqui só se diz qual é o objecto e mostra-se o âmbito.
//
// ⚠️ **E o âmbito diz-se e larga-se (P-30):** «só esta viagem» é um alternador à vista, nunca
//    um filtro escondido. Sem isso, um diário vazio por causa do alvo lê-se como «não
//    aconteceu nada» — que é o contrário do que aconteceu.
//
// ---------------------------------------------------------------------------
// ✅ §11 N · «O QUE MUDOU DESDE ONTEM» — 2026-09-16
// ---------------------------------------------------------------------------
//
// 📗 §11 N: *«A primeira pergunta de quem chega segunda-feira»*, «quase de graça do diário».
//
// ⚠️⚠️ **O «quase» foi MEDIDO, e não é zero.** O motor não mudou uma linha — as frases, o
//    agrupamento por gesto, o alvo e os dois verbos de desfazer são os mesmos. O que não saía
//    de graça foram os dois cortes: estavam escritos como interruptores de **duas** posições
//    (`hoje`/`tudo` e `eu`/`todos`) e a pergunta precisa de **três** em cada um —
//    **«desde ontem»** e **«tudo o que não fui eu»**. Ver o §6-bis de `apps/api/src/diario.ts`.
//
// ⚠️ **E o sinal «não fui eu» traz uma consequência de graça que vale mais do que o filtro:**
//    o veredicto do desfazer (peça 4) já recusa os gestos de outra pessoa com a razão escrita,
//    logo a lista nasce a dizer, linha a linha, «vê-se mas não se desfaz daqui».
//
// ⚠️⚠️ **NÃO partilha formulação com o `PlanoMudou.tsx` (melhoria O), e a razão é medida:** ele
//    compara **ETags** e por isso só pode dizer *«noutro sítio»* — não sabe quem, e o cabeçalho
//    dele di-lo por extenso («afirmar "outra pessoa" era pôr um valor plausível no lugar do
//    certo»). Esta lista lê `auditoria.utilizador_id` e **sabe** quem. São duas afirmações
//    diferentes sobre dois factos diferentes; colá-las numa só obrigava a que a mais fraca das
//    duas mandasse. 📌 O que faz sentido é o aviso dele **levar** aqui — e isso mexe no
//    `PlanoMudou.tsx`, que está fora desta fronteira; fica declarado por fazer.
// ============================================================================

import { useCallback, useContext, useEffect, useState } from 'react';
import { diaCurto } from '@xlv-gva-mpt/dominio';
import { LigacaoAoMapa } from './ligacao-ao-mapa.js';
import { janelaParaLer } from '../janela-do-ecra.js';
import { IconeFechar } from './icones.js';
import { BotaoActualizar } from './BotaoActualizar.js';
import { useSoAUltimaResposta } from './rede.js';
import { cantoInicial, useArrastavel, useEscape } from './janela.js';
// ⚠️⚠️ A leitura da recusa é a partilhada, a mesma do `Desfazer.tsx` — ver a nota junto a
//    `exigeMotivo` no `DesfazerDaLinha` (2026-09-29). Uma segunda leitura aqui era a que diverge.
import { recusaDoCorpo, useTravao, type CorpoDeRecusa } from './travao-no-ecra.js';
import { FRASE_SEM_DETALHE, fraseDaRecusa, type CorpoComDetalhe } from './frase-da-recusa.js';

/**
 * Espelham `QuemNoDiario` e `PeriodoDoDiario` em `apps/api/src/diario.ts` — §11 N, «o que mudou
 * desde ontem».
 *
 * ⚠️ São **três posições cada** e não dois alternadores, porque a pergunta de segunda-feira
 * precisa de «todos MENOS eu» e de «desde ontem» — duas posições que um booleano não tem. A
 * regra de cada uma vive no servidor; aqui só se diz qual está escolhida.
 */
type QuemNoDiario = 'eu' | 'todos' | 'outros';
/**
 * ⚠️ «Por utilizadores» (📗 cliente, 2026-09-29): uma pessoa escolhida entra no MESMO selector
 * do «quem», como `u:<id>` — são estados exclusivos, e um segundo selector ao lado podia dizer
 * «só eu» e «só a Ana» ao mesmo tempo sem nada o denunciar (a razão do comentário de baixo).
 */
type QuemEscolhido = QuemNoDiario | `u:${string}`;
/**
 * ⚠️ `entre` são as datas à escolha — o servidor lê `periodo=entre&desde=&ate=`. As posições
 * fixas espelham `PeriodoFixoDoDiario` em `apps/api/src/diario.ts`.
 */
type PeriodoDoDiario = 'hoje' | 'desde-ontem' | '7-dias' | '30-dias' | 'tudo' | 'entre';

/** Espelha `TrechoDoDiario` em `apps/api/src/diario.ts` — índices do dia do ano, como o mapa. */
interface Trecho {
  readonly viaturaId: string;
  /** 2026-10-30 · o número da viatura, que o servidor junta ao id (`numeros-de-viatura.ts`). */
  readonly viaturaNumero?: string;
  /** 2026-10-30 · Pedido 1 · os dias pela data, escritos pelo servidor com a fita do pedido. */
  readonly datas?: string;
  readonly dia0: number;
  readonly dia1: number;
}

/** Espelha `VeredictoSelectivo` em `apps/api/src/diario.ts`. */
interface Veredicto {
  readonly pode: boolean;
  readonly porque: string;
  readonly porCima: number;
}

/** Espelha `LinhaDoDiario` em `apps/api/src/diario.ts`. */
interface LinhaDoDiario {
  readonly em: string;
  readonly gestoId: string | null;
  readonly aproximado: boolean;
  readonly utilizadorId: string;
  readonly autor: string;
  readonly sistema: boolean;
  readonly frase: string;
  readonly accoes: readonly string[];
  readonly registos: number;
  /**
   * Espelha `TrechoDoDiario` — os dias que o gesto tocou, já lidos pelo servidor
   * (`diasDoGesto`, a ÚNICA leitura deles). ⚠️ Opcional só para um servidor mais velho do que
   * este ecrã; aqui não se deriva nada do `entidade_id`, que nem chega cá.
   */
  readonly dias?: readonly Trecho[];
  /**
   * Espelha `LinhaDoDiario.mudaOPlano` — decidido no servidor pela tabela do desfazer. `false`
   * (p. ex. «entrou no sistema») = sem «Reverter» e sem nota. ⚠️ Omisso (servidor mais velho)
   * conta como `true`: esconder o botão sobre o que não se sabe era calar a dúvida.
   */
  readonly mudaOPlano?: boolean;
  /** Espelha `LinhaDoDiario.nomesDasAccoes` — o nome legível de cada acção, pela ordem de `accoes`. */
  readonly nomesDasAccoes?: readonly string[];
  /** ⚠️ `undefined` = o servidor não anotou. Ver `LinhaDoDiario.desfazer` no servidor. */
  readonly desfazer?: Veredicto;
}

/** Espelha `AntesDeDesfazerDoDiario` em `apps/api/src/desfazer-selectivo.ts`. */
interface AntesDeDesfazer {
  readonly em: string;
  readonly descricao: string;
  readonly veredicto: Veredicto;
  readonly travados: { readonly dias: number; readonly frase: string; readonly impedem: boolean } | null;
}

function eAntesDeDesfazer(x: unknown): x is AntesDeDesfazer {
  if (typeof x !== 'object' || x === null) return false;
  const v = (x as { veredicto?: unknown }).veredicto;
  return typeof v === 'object' && v !== null && typeof (v as { pode?: unknown }).pode === 'boolean';
}

interface Resposta {
  readonly linhas: readonly LinhaDoDiario[];
}

/**
 * ⚠️ A forma verifica-se; um `as` não a verifica — a mesma disciplina de `Desfazer.tsx`. Um
 * 401/403 devolve outra forma, e sem esta guarda a lista lia `undefined` como «diário vazio»,
 * que é indistinguível de «não tenho acesso».
 */
function eResposta(x: unknown): x is Resposta {
  return typeof x === 'object' && x !== null && Array.isArray((x as { linhas?: unknown }).linhas);
}

function horaCurta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}

/**
 * A viagem sobre a qual se pergunta — §5.6 exigência 3.
 *
 * ⚠️ **As três chaves vão TODAS**, e não só a viatura: a auditoria grava umas acções pela
 * viatura e o dia (`mudar_executor`), outras pelo pedido (`reservar`, `alterar_pedido`) e uma
 * pela referência (`alterar_cor_corrida`). Mandar só uma delas calava as outras duas famílias
 * — e «tudo o que aconteceu» com dois terços a faltar é pior do que não filtrar.
 */
export interface AlvoDoDiarioNoEcra {
  /**
   * ⚠️⚠️ **OPCIONAIS porque nem toda a pergunta nasce numa CÉLULA.** A do mapa traz sempre a
   * viatura e o intervalo da corrida; a do `Detalhe` nasce de um **pedido** e não sabe nem
   * uma nem outro — as datas dele são do PEDIDO e os índices do dia são da grelha.
   *
   * 📌 **Não se inventa um intervalo para preencher o campo.** O servidor filtra por dias **por
   * cima** da chave (`tocaOAlvo`, `diario.ts` §5), logo uma janela adivinhada **cortava**
   * registos que pertencem ao pedido. `undefined` diz «não estreites por dias», que é a
   * verdade; um intervalo a fingir dava uma lista plausível no lugar da certa.
   */
  readonly viaturaId?: string | undefined;
  /** Índices do dia do ano — o intervalo da CORRIDA, não o dia clicado. */
  readonly dia0?: number | undefined;
  readonly dia1?: number | undefined;
  readonly referencia?: string | undefined;
  readonly pedidoId?: string | undefined;
  /** Como a viagem se diz a quem lê: «901 · 1–3 Jan · R-0001». */
  readonly rotulo: string;
}

/**
 * ⚠️⚠️ **OS PARÂMETROS DO ALVO, NUMA SÓ FORMULAÇÃO.** Duas rotas os recebem — o diário
 * (`/api/plano/diario`) e a cadeia do «Como estava» (`/api/plano/como-estava`) — e as duas
 * leem-nos com o MESMO `alvoDaConsulta` do lado do servidor.
 *
 * 📌 Estavam escritos duas vezes neste ficheiro, e a segunda cópia mandava `dia0`/`dia1`
 * **sempre**: com um alvo sem dias escrevia `undefined` no URL, e `Number('undefined')` é `NaN`.
 * Duas listas do mesmo objecto com filtros diferentes é a divergência que a regra 8 recusa.
 */
function paramsDoAlvo(alvo: AlvoDoDiarioNoEcra): URLSearchParams {
  const params = new URLSearchParams();
  if (alvo.viaturaId !== undefined && alvo.viaturaId !== '') {
    params.set('viatura', alvo.viaturaId);
  }
  // ⚠️ Os dois juntos ou nenhum: o servidor exige o par para cortar (`alvo.dia0 === null ||
  //    alvo.dia1 === null` não corta), e meio intervalo não é um intervalo.
  if (alvo.dia0 !== undefined && alvo.dia1 !== undefined) {
    params.set('dia0', String(alvo.dia0));
    params.set('dia1', String(alvo.dia1));
  }
  if (alvo.referencia !== undefined && alvo.referencia !== '') {
    params.set('referencia', alvo.referencia);
  }
  if (alvo.pedidoId !== undefined && alvo.pedidoId !== '') {
    params.set('pedido', alvo.pedidoId);
  }
  return params;
}

/** Espelha `MomentoDaViagem` em `apps/api/src/como-estava.ts`. */
interface Momento {
  readonly em: string;
  readonly frase: string;
  readonly autor: string;
  readonly sistema: boolean;
  readonly gestos: number;
  readonly reponivel: boolean;
  readonly porque: string;
  readonly travadoPor: string | null;
}

/** Espelha `Reposicao` em `apps/api/src/como-estava.ts`. */
interface Reposicao {
  readonly momentoPedido: string;
  readonly momentoAlcancado: string | null;
  readonly completa: boolean;
  readonly porqueParou: string | null;
  readonly previstos: number;
  readonly revertidos: number;
  readonly diasPreservados: number;
}

function eReposicao(x: unknown): x is Reposicao {
  return (
    typeof x === 'object' && x !== null && typeof (x as { completa?: unknown }).completa === 'boolean'
  );
}

function eCadeia(x: unknown): x is { readonly momentos: readonly Momento[] } {
  return typeof x === 'object' && x !== null && Array.isArray((x as { momentos?: unknown }).momentos);
}

/**
 * ⚠️⚠️ **PEÇA 5 · «COMO ESTAVA» — repor a viagem para o instante ANTES deste gesto.**
 *
 * 📗 §5.5: *«Uma viagem, um momento → inverte os gestos dela desde aí, por ordem inversa. Só
 * toca naquele objecto.»*
 *
 * ⚠️⚠️ **O verbo só aparece onde acrescenta alguma coisa, e a regra é exacta:**
 *
 * | estado | o que se mostra | porquê |
 * |---|---|---|
 * | `gestos === 1` | **nada** | «repor até antes deste gesto» com um gesto só **é** o «Reverter» que já está na linha. 📌 É o teste que valida o desenho (§5.7): o que já se escreve não se escreve outra vez |
 * | `gestos >= 2` | o botão, com **quantos** vai desfazer | é o único caso em que o verbo é outro |
 * | não se repõe **e** a peça 4 também recusa | **nada** | a razão dela já está na linha; duas frases a dizer o mesmo é a parede de prosa que esta casa mede |
 * | não se repõe **e** a peça 4 aceita | a razão, sem botão | é o ÚNICO caso em que a ausência do botão ficaria por explicar — e é sempre a barreira |
 *
 * ⚠️ A decisão vive toda no servidor (`como-estava.ts`); aqui não se calcula nada. Uma segunda
 * tabela no navegador era a formulação que diverge.
 */
function ComoEstavaDaLinha({
  linha,
  momento,
  alvo,
  recarregar,
}: {
  readonly linha: LinhaDoDiario;
  /**
   * 2026-10-27 · `undefined` quando a cadeia relida já não traz este momento. ⚠️ O componente
   * fica MONTADO mesmo assim: era a montagem condicional (`cadeia.get(l.em) !== undefined`) que o
   * desmontava depois da reposição — e levava com ele o «a reposição PAROU a meio», que é a frase
   * mais importante desta peça. Sem momento e sem resultado, não mostra nada.
   */
  readonly momento: Momento | undefined;
  readonly alvo: AlvoDoDiarioNoEcra;
  readonly recarregar: () => void;
}): React.JSX.Element | null {
  const [aConfirmar, definirAConfirmar] = useState(false);
  const [aEscrever, definirAEscrever] = useState(false);
  const [motivo, definirMotivo] = useState('');
  const [erro, definirErro] = useState<string | null>(null);
  const [feito, definirFeito] = useState<Reposicao | null>(null);

  async function repor(): Promise<void> {
    definirAEscrever(true);
    definirErro(null);
    try {
      const r = await fetch('/api/plano/como-estava', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          viatura: alvo.viaturaId,
          dia0: alvo.dia0,
          dia1: alvo.dia1,
          referencia: alvo.referencia ?? '',
          pedido: alvo.pedidoId ?? '',
          ate: linha.em,
          // ⚠️ **Sempre com motivo, e é uma decisão declarada.** Uma reposição são N escritas
          //    numa decisão só; D-007 manda avisar e deixar avançar **com motivo**, e aqui não
          //    há pré-verificação dos travões (custa duas consultas por gesto). Um controlo, o
          //    mesmo sempre — e o que ficar por reverter vem nomeado na resposta.
          aceitaTravoes: true,
          motivo: motivo.trim(),
        }),
      });
      const corpo: unknown = await r.json().catch(() => null);
      if (!r.ok || !eReposicao(corpo)) {
        // ⚠️ **A recusa lê-se pela regra partilhada** (`recusaDoCorpo`), como no «Reverter».
        //    📌 Aqui não há campo que falte abrir: o motivo é SEMPRE pedido antes de enviar e
        //    vai sempre com `aceitaTravoes: true` — logo uma recusa que peça motivo
        //    (`motivo_obrigatorio`, um travão forçável) chega com o campo já à vista e
        //    preenchido, e o que falta é só a frase, que é a do servidor.
        definirErro(
          recusaDoCorpo(
            corpo !== null && typeof corpo === 'object' ? (corpo as CorpoDeRecusa) : null,
            r.status,
            'a reposição',
          ).mensagem,
        );
        return;
      }
      definirFeito(corpo);
      recarregar();
    } catch (e: unknown) {
      definirErro(e instanceof Error ? e.message : String(e));
    } finally {
      definirAEscrever(false);
    }
  }

  // ⚠️⚠️ **O RESULTADO INCOMPLETO É A COISA MAIS IMPORTANTE DESTE COMPONENTE.** Uma escrita
  //    PARCIAL anunciada como completa é o gémeo de «uma escrita que não se vê» — o defeito por
  //    que esta casa reverteu um gesto inteiro (`e7cae6d7`). Logo quando a reposição não chega
  //    ao fim diz-se onde a viagem FICOU, e diz-se porquê, no sítio do botão.
  if (feito !== null) {
    if (feito.completa) {
      return (
        <p className="desfazer-razao">
          <strong>Reposta:</strong> {String(feito.revertidos)} gesto(s) desfeito(s)
          {feito.diasPreservados > 0
            ? ` · ${String(feito.diasPreservados)} dia(s) ficaram como estavam (já passaram ou já foram fechados)`
            : ''}
          .
        </p>
      );
    }
    return (
      <>
        <p className="desfazer-erro">
          <strong>A reposição PAROU a meio.</strong>{' '}
          {feito.momentoAlcancado === null
            ? 'Nada foi desfeito — a viagem está como estava antes de clicar.'
            : `Desfez ${String(feito.revertidos)} de ${String(feito.previstos)} gesto(s): a viagem está agora como estava às ${horaCurta(feito.momentoAlcancado)}, e não às ${horaCurta(feito.momentoPedido)} como pediu.`}
        </p>
        <p className="desfazer-aviso">{feito.porqueParou}</p>
      </>
    );
  }

  if (momento === undefined) return null;

  if (!momento.reponivel) {
    // 📌 Só quando a peça 4 não recusou — senão a razão dela já está imediatamente acima, e
    //    repetir a mesma coisa por outras palavras é o que faz ninguém ler nenhuma das duas.
    if (linha.desfazer?.pode !== true) return null;
    return (
      <p className="desfazer-razao">
        <strong>não se repõe até aqui</strong> — {momento.porque}
      </p>
    );
  }

  // §5.7: com um gesto só, «como estava» É o «desfazer este gesto» que já está na linha.
  if (momento.gestos <= 1) return null;

  if (!aConfirmar) {
    return (
      <div className="desfazer-accoes faixa-quebra">
        <button
          type="button"
          className="btn btn--peq"
          onClick={() => definirAConfirmar(true)}
        >
          Como estava antes disto ({String(momento.gestos)} gestos)
        </button>
      </div>
    );
  }

  return (
    <>
      <p className="desfazer-frase">
        <strong>Repor a viagem</strong> como estava antes de «{momento.frase}» — desfaz{' '}
        {String(momento.gestos)} gesto(s) desta viagem, do mais recente para trás.
      </p>
      {/* ⚠️ O que a reposição NÃO promete, dito ANTES de se clicar e não depois. */}
      <p className="desfazer-aviso">
        Só toca nesta viagem. Os dias que já passaram, ou que já foram fechados, ficam como
        estão. Se uma das inversões falhar, a reposição pára aí e diz onde a viagem ficou.
      </p>
      <label className="desfazer-motivo">
        Motivo
        <input
          type="text"
          value={motivo}
          onChange={(e) => definirMotivo(e.target.value)}
          placeholder="porque é que se volta atrás"
        />
      </label>
      {erro !== null ? <p className="desfazer-erro">{erro}</p> : null}
      <div className="desfazer-accoes faixa-quebra">
        <button type="button" className="btn btn--peq" onClick={() => definirAConfirmar(false)}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn--peq btn--principal"
          disabled={aEscrever || motivo.trim() === ''}
          onClick={() => void repor()}
        >
          {aEscrever ? 'A repor…' : 'Repor'}
        </button>
      </div>
    </>
  );
}

/**
 * ⚠️⚠️ **PEÇA 4 · O DESFAZER DE UMA LINHA DO DIÁRIO.**
 *
 * 📗 §5.5: *«Selectivo · **a linha vem marcada ANTES** quando não há inverso limpo. Clicar e
 * levar recusa ensina a não confiar no botão.»*
 *
 * ⚠️⚠️ **A MARCA É O PRODUTO, e vem do SERVIDOR.** Nada aqui decide se um gesto se desfaz — a
 * regra (é meu? já foi desfeito? tem inverso? alguém construiu por cima?) vive em
 * `apps/api/src/desfazer-selectivo.ts`, um sítio só. Uma segunda tabela no navegador era a
 * formulação que diverge, e *«a que divergir é a que ninguém corre»* — é a mesma nota que o
 * `Desfazer.tsx` traz no cabeçalho, pela mesma razão.
 *
 * ⚠️ **P-30: a linha que não se desfaz FICA na lista, marcada.** Esconder o que não se pode
 * fazer faz o diário parecer mais curto do que é, e a pergunta «e este, porque não?» deixa de
 * ter onde ser feita.
 *
 * ⚠️⚠️ **E o clique RE-PERGUNTA ao servidor antes de gravar.** A lista pode estar na mão há dez
 * minutos; entre vê-la e clicar, outra pessoa pode ter escrito nos mesmos dias. É a mesma
 * disciplina do `nao_e_o_topo` do `Ctrl+Z`, com a diferença de que a resposta chega **antes** da
 * escrita e não depois. É também aí que aparecem os travões (dias que já passaram, serviços
 * fechados), que só a base sabe.
 */
/**
 * 📗 2026-10-21 · «muito texto»: um registo longo (o motivo inteiro de uma divergência chega a seis
 * linhas) mostra o INÍCIO e abre por inteiro com ▸. Curto, fica como estava. ⚠️ O texto completo
 * continua no DOM — nada se perde, muda de sítio.
 */
const FRASE_CURTA = 160;
function FraseDoDiario({ frase }: { readonly frase: string }): React.JSX.Element {
  if (frase.length <= FRASE_CURTA) return <p className="diario-frase">{frase}</p>;
  const corte = frase.lastIndexOf(' ', FRASE_CURTA);
  const inicio = frase.slice(0, corte > 80 ? corte : FRASE_CURTA);
  return (
    <details className="diario-frase-longa">
      <summary className="diario-frase">{inicio}…</summary>
      <p className="diario-frase">{frase}</p>
    </details>
  );
}

function DesfazerDaLinha({
  linha,
  recarregar,
}: {
  readonly linha: LinhaDoDiario;
  readonly recarregar: () => void;
}): React.JSX.Element | null {
  const [antes, definirAntes] = useState<AntesDeDesfazer | null>(null);
  const [aPerguntar, definirAPerguntar] = useState(false);
  const [aEscrever, definirAEscrever] = useState(false);
  const [motivo, definirMotivo] = useState('');
  const [erro, definirErro] = useState<string | null>(null);
  const [feito, definirFeito] = useState<string | null>(null);
  // ⚠️ Antes dos `return` antecipados — um *hook* não pode depender de um ramo.
  const travao = useTravao();

  // 2026-10-27 · O RESULTADO ANTES DE QUALQUER OUTRO RAMO. Depois de reverter, a releitura do
  //    diário traz esta linha com `desfazer.pode = false` (já foi revertida) — e o ramo `!v.pode`,
  //    que vinha primeiro, trocava «Desfeito: N revertido(s) · M dia(s) ficaram como estavam» por
  //    um «Reverter» cinzento. Era a prova do que se acabou de fazer a desaparecer logo a seguir.
  if (feito !== null) {
    return (
      <p className="desfazer-razao">
        <strong>Desfeito:</strong> {feito}
      </p>
    );
  }

  const v = linha.desfazer;
  if (v === undefined) return null;
  // ⚠️⚠️ **Um gesto que não muda o plano não tem «Reverter» nem nota** (📗 cliente, 2026-09-30:
  //    o «Reverter» e a nota apareciam debaixo de «entrou no sistema»). Não é um botão escondido
  //    sobre uma regra — é a ausência de destino: não há nada no plano para devolver ao que era.
  //    Quem o decide é o servidor (`mudaOPlano`, pela tabela do desfazer), nunca este ecrã.
  if (linha.mudaOPlano === false) return null;

  // ⚠️ **A LINHA MARCADA — e a razão POR EXTENSO, não só um botão cinzento.** Um «não» sem razão
  //    manda procurar o defeito no sítio errado; este diz o que fazer em vez disto.
  // ⚠️⚠️ **E o botão FICA, desactivado** (📗 cliente, 2026-09-29: *«botões em cada acção do tipo
  //    reverter»*). Um botão que só existe nalgumas linhas lê-se como «esta acção não se pode
  //    reverter por defeito do ecrã»; desactivado e com a razão ao lado lê-se como a regra que é
  //    (P-30). A razão é a do servidor — a de `inverso-da-accao.ts` quando a acção não tem
  //    inverso —, nunca uma frase escrita aqui.
  if (!v.pode) {
    return (
      <div className="desfazer-accoes faixa-quebra">
        <button type="button" className="btn btn--peq" disabled title={v.porque}>
          Reverter
        </button>
        {/* 📗 2026-10-21 · «muito texto»: a razão (quatro linhas, repetidas em cada registo) passa
            para trás de um ▸ — o botão desactivado continua à vista, que é a decisão de 2026-09-29. */}
        <details className="desfazer-razao">
          <summary>porquê não</summary>
          <strong>não se desfaz daqui</strong> — {v.porque}
        </details>
      </div>
    );
  }

  async function perguntar(): Promise<void> {
    definirAPerguntar(true);
    definirErro(null);
    try {
      const r = await fetch(`/api/plano/desfazer?em=${encodeURIComponent(linha.em)}`);
      const corpo: unknown = await r.json().catch(() => null);
      if (!r.ok || !eAntesDeDesfazer(corpo)) {
        // ⚙ Para quem programa: sem o estado HTTP no ecrã — `frase-da-recusa.ts`.
        definirErro(fraseDaRecusa(corpo !== null && typeof corpo === 'object' ? (corpo as CorpoComDetalhe) : null));
        return;
      }
      definirAntes(corpo);
    } catch (e: unknown) {
      definirErro(e instanceof Error ? e.message : String(e));
    } finally {
      definirAPerguntar(false);
    }
  }

  // ⚠️⚠️ **A PRÉ-VERIFICAÇÃO NÃO VÊ TUDO O QUE PEDE MOTIVO (2026-09-29)** — a mesma correcção do
  //    `Desfazer.tsx` (`2cf20304`). 📗 *«confirmados pede motivo, mas não existe caixa para
  //    indicar o motivo»*. O `antes.travados` diz os travões que o servidor mediu ao clicar em
  //    «Reverter»; a escrita pode recusar por mais razões que pedem motivo — a espera que uma
  //    rotação recriada impõe ao grupo novo (`motivo_obrigatorio`, que o inverso de um «pôr em
  //    espera» passou a poder dar), ou um travão que nasceu entre a pergunta e o clique. 📌 Aí o
  //    campo não aparecia e o botão repetia a mesma recusa. A resposta passa a ser lida por
  //    `recusaDoCorpo`, e o campo abre-se; o reenvio leva a aceitação E o motivo.
  const exigeMotivo =
    (antes?.travados != null && !antes.travados.impedem) || travao.forcavel;

  async function gravar(): Promise<void> {
    definirAEscrever(true);
    definirErro(null);
    try {
      const r = await fetch('/api/plano/desfazer', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          em: linha.em,
          // ⚠️⚠️ **`selectivo` escolhe o PORTÃO no servidor, e não uma escrita diferente.** Sem
          //    ele a rota aplica o LIFO estrito e recusa com `nao_e_o_topo` — que é verdade e
          //    é a razão errada para quem escolheu uma linha do diário.
          selectivo: true,
          aceitaTravoes: exigeMotivo,
          motivo: motivo.trim() === '' ? null : motivo.trim(),
        }),
      });
      const corpo = (await r.json()) as Record<string, unknown>;
      if (!r.ok) {
        // ⚙ Para quem programa: só a frase — ver `frase-da-recusa.ts`.
        definirErro(fraseDaRecusa(corpo));
        travao.registar(recusaDoCorpo(corpo as CorpoDeRecusa, r.status, 'reverter'));
        return;
      }
      travao.limpar();
      // ⚠️⚠️ **Recarrega o diário E o mapa.** 📌 *«Uma escrita que não se vê é indistinguível de
      //    uma que não aconteceu»* — e um diário que não mostra o desfazer que acabou de
      //    acontecer é exactamente isso, num ecrã cuja razão de ser é mostrar o que aconteceu.
      definirFeito(
        `${String(corpo['revertidos'] ?? 0)} revertido(s)` +
          (Number(corpo['diasPreservados'] ?? 0) > 0
            ? ` · ${String(corpo['diasPreservados'])} dia(s) ficaram como estavam (já passaram ou já foram fechados)`
            : ''),
      );
      recarregar();
    } catch (e: unknown) {
      definirErro(e instanceof Error ? e.message : String(e));
    } finally {
      definirAEscrever(false);
    }
  }

  if (antes === null) {
    return (
      <div className="desfazer-accoes faixa-quebra">
        {erro !== null ? <p className="desfazer-erro">{erro}</p> : null}
        <button
          type="button"
          className="btn btn--peq"
          disabled={aPerguntar}
          onClick={() => void perguntar()}
        >
          {aPerguntar ? 'a verificar…' : 'Reverter'}
        </button>
      </div>
    );
  }

  // ⚠️ O servidor pode ter mudado de ideias entre a lista e o clique — e então diz-se, no sítio
  //    onde o botão estava, em vez de se deixar um botão que leva recusa.
  if (!antes.veredicto.pode) {
    return (
      <p className="desfazer-razao">
        <strong>entretanto deixou de se poder desfazer</strong> — {antes.veredicto.porque}
      </p>
    );
  }

  return (
    <>
      <p className="desfazer-frase">
        <strong>Desfazer:</strong> {antes.descricao}
      </p>
      {antes.travados != null ? (
        <p className="desfazer-aviso">
          ⚠️ {antes.travados.frase}.{' '}
          {antes.travados.impedem
            ? 'Os que já passaram ou já foram fechados não se desfazem — reverte-se o resto.'
            : 'Avançar apesar disto exige motivo.'}
        </p>
      ) : null}
      {exigeMotivo ? (
        <label className="desfazer-motivo">
          Motivo
          <input
            type="text"
            value={motivo}
            onChange={(e) => definirMotivo(e.target.value)}
            placeholder="porque é que se volta atrás"
          />
        </label>
      ) : null}
      {erro !== null ? <p className="desfazer-erro">{erro}</p> : null}
      <div className="desfazer-accoes faixa-quebra">
        <button type="button" className="btn btn--peq" onClick={() => definirAntes(null)}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn--peq btn--principal"
          disabled={aEscrever || (exigeMotivo && motivo.trim() === '')}
          onClick={() => void gravar()}
        >
          {aEscrever ? 'A reverter…' : 'Reverter'}
        </button>
      </div>
    </>
  );
}

/**
 * ⚠️⚠️ **OS DIAS DO REGISTO E O «IR PARA»** — 📗 cliente, 2026-09-29: *«aparecer também os dias
 * nos registos»* e *«botões em cada acção do tipo … ir para»*.
 *
 * Os dias chegam PRONTOS do servidor (`diasDoGesto`); aqui só se dizem e se oferece o salto. Um
 * «Ir para» por pedaço, porque um gesto pode tocar duas viaturas (a origem e o destino de uma
 * troca) e ir só a uma respondia a metade da pergunta.
 *
 * ⚠️⚠️ **Sem dias, NÃO há nada — nem botão desactivado, nem frase** (📗 cliente, 2026-09-30: *«o
 * "Ir para" só deve aparecer onde faça sentido, não em todo o tipo de acção, por exemplo
 * "entrar"»*). A versão de 2026-09-29 deixava-o desactivado com «sem dias — este registo não
 * nomeia viatura nem dias» debaixo de cada «entrou no sistema». 📌 Esconder aqui é correcto
 * porque não há destino possível — o registo não nomeia sítio nenhum. É diferente do caso de
 * baixo: com dias e **sem mapa** o destino existe e falta só o mapa, e aí o botão fica
 * desactivado com a razão.
 */
function DiasDaLinha({
  dias,
  verNoMapa,
  nomeDoDia,
}: {
  readonly dias: readonly Trecho[];
  readonly verNoMapa: ((viaturaId: string, dia0: number, dia1: number) => void) | undefined;
  readonly nomeDoDia: ((dia: number) => string) | undefined;
}): React.JSX.Element | null {
  // 🔴 2026-10-30 · Pedido 1 · o dia pela DATA, sempre — sem o mapa, com a fita deste ecrã (a que o
  //    pedido levou no cabeçalho) ou, antes de o mapa chegar, a fita de hoje (a do servidor sem
  //    cabeçalho). «dia 203 do ano» obrigava a contar dias. Formulação única: `diaCurto`.
  const janela = janelaParaLer();
  const dizer = (d: number): string => (nomeDoDia === undefined ? diaCurto(janela, d) : nomeDoDia(d));
  const numero = (t: Trecho): string => t.viaturaNumero ?? t.viaturaId;
  const semMapa = 'este diário não foi aberto a partir do mapa — não há mapa para onde ir';
  if (dias.length === 0) return null;
  return (
    <div className="diario-dias faixa-quebra">
      {dias.map((t) => (
        <span key={`${t.viaturaId}:${String(t.dia0)}`} className="diario-trecho">
          <span>
            {/* 2026-10-30 · o número da viatura (o servidor junta-o) — defeito 2. */}
            {numero(t)} · {t.datas ?? (t.dia1 !== t.dia0 ? `${dizer(t.dia0)} a ${dizer(t.dia1)}` : dizer(t.dia0))}
          </span>
          <button
            type="button"
            className="btn btn--peq"
            disabled={verNoMapa === undefined}
            title={verNoMapa === undefined ? semMapa : `levar o mapa a ${numero(t)}, a estes dias`}
            aria-label={dias.length > 1 ? `Ir para ${numero(t)}` : 'Ir para'}
            onClick={() => verNoMapa?.(t.viaturaId, t.dia0, t.dia1)}
          >
            Ir para
          </button>
        </span>
      ))}
    </div>
  );
}

export function Diario({
  fechar,
  alvo,
  recarregarMapa,
  quemInicial,
  verNoMapa,
  nomeDoDia,
}: {
  readonly fechar: () => void;
  /** `undefined` = o diário geral, que é como a barra de topo o abre. */
  readonly alvo?: AlvoDoDiarioNoEcra | undefined;
  /**
   * ⚠️ Quem se mostra **por omissão**. `undefined` = a regra de sempre (`eu` sem alvo, `todos`
   * com alvo). Existe para quem abre o diário a partir de uma pergunta que já estreitou o
   * «quem» — hoje é o aviso «o plano mudou noutro sítio» (`PlanoMudou.tsx`), que abre em
   * `outros` porque acabou de dizer que a mudança **não foi deste separador**.
   */
  readonly quemInicial?: QuemNoDiario | undefined;
  /**
   * ⚠️ O mapa por trás tem de recarregar depois de um desfazer — senão a grelha continua a
   * mostrar o que já não está lá, que é a queixa exacta que este projecto registou no rearranjo
   * («aceitar um rearranjo não mexia no mapa»).
   */
  readonly recarregarMapa?: (() => void) | undefined;
  /**
   * ⚠️ «Ir para» (📗 cliente, 2026-09-29) — o MESMO mecanismo dos outros «Ver no mapa»: quem abre
   * a janela passa o `irPara` do `Mapa.tsx` (`verNoMapa={irPara}`, como a cópia e o «trazer de
   * espera»). Ausente = o botão fica desactivado e diz porquê, nunca some.
   */
  readonly verNoMapa?: ((viaturaId: string, dia0: number, dia1: number) => void) | undefined;
  /**
   * Como um índice do dia se diz a quem lê — o `dataCurtaDoDia` do mapa, que é quem sabe o ano.
   * ⚠️ Ausente, diz-se «dia N do ano»: o ano vive no mapa, e adivinhá-lo aqui era pôr uma data
   * plausível no lugar da certa (um dia de desvio em cada ano bissexto).
   */
  readonly nomeDoDia?: ((dia: number) => string) | undefined;
}): React.JSX.Element {
  // ⚠️ A propriedade passada à mão ganha; sem ela, a ligação que o `Mapa` fornece
  //    (`ligacao-ao-mapa.ts`) — é o que faz as cinco aberturas do Diário dizerem os dias.
  const ligacao = useContext(LigacaoAoMapa);
  const verNoMapaEfectivo = verNoMapa ?? ligacao?.verNoMapa;
  const nomeDoDiaEfectivo = nomeDoDia ?? ligacao?.nomeDoDia;
  const { posicao, aoPressionar, aArrastar } = useArrastavel(() =>
    // Cascata das janelas de topo (`cantoInicial`, `janela.ts`): ao lado do «Desfazer».
    cantoInicial(
      'direita',
      { largura: 560, altura: 560 },
      { largura: window.innerWidth, altura: window.innerHeight },
      368,
      64,
    ),
  );
  useEscape(fechar);

  // §5.6 exigência 4: por omissão, «os meus gestos de hoje» — os dois selectores ALARGAM.
  //
  // ⚠️⚠️ **COM ALVO A OMISSÃO É A OPOSTA, e é deliberado.** A pergunta da célula é «tudo o que
  //    aconteceu a esta viagem» — uma viagem planeada na semana passada por outra pessoa não
  //    tem um único gesto meu de hoje, logo a omissão geral respondia «nada» a uma viagem com
  //    história. 📌 O que trava a lista aqui é o **alvo**, não a data: são duas perguntas
  //    diferentes, e cada uma tem a sua omissão. Os dois selectores continuam à vista e
  //    no valor largo — quem quiser estreitar, estreita (P-30).
  //
  // ⚠️⚠️ **ERAM DOIS ALTERNADORES E PASSAM A DOIS SELECTORES (§11 N, 2026-09-16), e a razão é
  //    mecânica e não estética:** cada corte passou a ter **três** posições e uma caixa de
  //    marcar só sabe dizer duas. Um terceiro alternador ao lado («só o que não fui eu») podia
  //    ficar marcado ao mesmo tempo que «todos» estava desmarcado, e o ecrã afirmava dois
  //    filtros contraditórios sem nada o denunciar. Três estados exclusivos = um controlo.
  // ⚠️ `quemInicial` **só sobrepõe a OMISSÃO, e nada mais** — o selector continua a mandar a
  //    partir do primeiro clique. Sem ele o aviso «o plano mudou noutro sítio» abria o diário
  //    em «os MEUS gestos», que é a lista que garantidamente **não** contém o que ele acabou de
  //    anunciar. 📌 Omisso, reproduz a regra anterior palavra por palavra — e há prova disso.
  const [quem, definirQuem] = useState<QuemEscolhido>(
    quemInicial ?? (alvo !== undefined ? 'todos' : 'eu'),
  );
  const [periodo, definirPeriodo] = useState<PeriodoDoDiario>(
    alvo !== undefined ? 'tudo' : 'hoje',
  );
  /**
   * ⚠️ A pergunta de segunda-feira (melhoria N do §11) são as **duas** posições novas ao mesmo
   * tempo — «toda a gente menos eu» e «desde ontem». Desde 2026-09-30 pede-se nos dois
   * selectores (o botão próprio saiu — ver a nota junto aos filtros); o vazio dela continua a ter
   * frase própria, e é para isso que esta combinação se reconhece.
   */
  const desdeOntemSemMim = quem === 'outros' && periodo === 'desde-ontem';
  /** As pontas das datas à escolha (`AAAA-MM-DD`, o valor de um `<input type="date">`). */
  const [desde, definirDesde] = useState('');
  const [ate, definirAte] = useState('');
  /** O tipo de acção (`auditoria.acao`), ou `''` para todas. */
  const [accao, definirAccao] = useState('');
  /**
   * ⚠️⚠️ **As opções de utilizador e de tipo VÊM DOS DADOS**, e acumulam-se: tudo o que já chegou
   * nesta janela fica escolhível. 📌 Derivá-las só da ÚLTIMA resposta encolhia o selector à
   * pessoa escolhida — escolher a Ana tirava o Rui da lista, e voltar atrás exigia saber que o
   * «toda a gente» o repõe. Uma lista escrita à mão era a formulação que envelhece.
   */
  const [pessoasVistas, definirPessoasVistas] = useState<ReadonlyMap<string, string>>(new Map());
  const [accoesVistas, definirAccoesVistas] = useState<ReadonlyMap<string, string>>(new Map());
  /**
   * ⚠️ O alvo LARGA-SE, e é a outra metade de o anunciar. Sem isto, quem abrisse o diário de
   * uma viagem sem registos ficava sem saber se o plano está calado ou se é o filtro a apertar.
   */
  const [soEstaViagem, definirSoEstaViagem] = useState(true);
  const alvoActivo = alvo !== undefined && soEstaViagem ? alvo : null;
  const [d, definirD] = useState<Resposta | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [aCarregar, definirACarregar] = useState(false);
  /**
   * ⚠️⚠️ **PEÇA 5 · a cadeia da viagem, por `em`.** Vem de uma rota própria e não do diário, e
   * é deliberado: a cadeia é sempre **a história INTEIRA da viagem, de toda a gente** — os
   * filtros «meus»/«hoje» do diário estreitam a LISTA e não podem estreitar a decisão, senão um
   * gesto de outra pessoa deixava de contar como barreira só por estar escondido.
   */
  const [cadeia, definirCadeia] = useState<ReadonlyMap<string, Momento>>(new Map());

  // 🔴 2026-10-22 · só a ÚLTIMA leitura escreve: mudar de filtro depressa deixava uma resposta
  //    antiga, chegada depois, tapar a nova (`useSoAUltimaResposta`, rede.ts).
  const comecarLeitura = useSoAUltimaResposta();
  const comecarCadeia = useSoAUltimaResposta();
  const carregar = useCallback(async () => {
    const aindaEAUltima = comecarLeitura();
    definirACarregar(true);
    definirErro(null);
    try {
      const params = alvoActivo === null ? new URLSearchParams() : paramsDoAlvo(alvoActivo);
      // ⚠️ `todos=1` é a grafia JÁ PUBLICADA de «toda a gente», e mantém-se: a regra vive toda
      //    no servidor (`quemDaConsulta`), que lê as duas. Trocá-la aqui por `quem=todos` não
      //    acrescentava nada e partia quem já a manda.
      if (quem === 'todos') params.set('todos', '1');
      if (quem === 'outros') params.set('quem', 'outros');
      if (quem.startsWith('u:')) params.set('utilizador', quem.slice(2));
      if (periodo !== 'hoje') params.set('periodo', periodo);
      if (periodo === 'entre') {
        if (desde !== '') params.set('desde', desde);
        if (ate !== '') params.set('ate', ate);
      }
      if (accao !== '') params.set('accao', accao);
      const r = await fetch(`/api/plano/diario?${params.toString()}`);
      const corpo: unknown = await r.json().catch(() => null);
      if (!aindaEAUltima()) return;
      if (!r.ok) {
        const detalhe =
          corpo !== null && typeof corpo === 'object' && 'detalhe' in corpo
            ? String((corpo as { detalhe?: unknown }).detalhe)
            : null;
        definirErro(detalhe ?? FRASE_SEM_DETALHE);
        return;
      }
      if (!eResposta(corpo)) {
        definirErro('o diário devolveu uma resposta que o ecrã não sabe ler. Recarregue a página.');
        return;
      }
      definirD(corpo);
      definirPessoasVistas((antes) => {
        const m = new Map(antes);
        for (const l of corpo.linhas) m.set(l.utilizadorId, l.autor);
        return m.size === antes.size ? antes : m;
      });
      definirAccoesVistas((antes) => {
        const c = new Map(antes);
        // ⚠️ Sem nome do servidor (servidor mais velho), diz-se «outra acção» — nunca a chave crua.
        for (const l of corpo.linhas) {
          l.accoes.forEach((a, i) => c.set(a, l.nomesDasAccoes?.[i] ?? c.get(a) ?? 'outra acção'));
        }
        return c.size === antes.size && [...c].every(([k, v]) => antes.get(k) === v) ? antes : c;
      });
    } catch (e: unknown) {
      if (aindaEAUltima()) definirErro(e instanceof Error ? e.message : String(e));
    } finally {
      if (aindaEAUltima()) definirACarregar(false);
    }
    // ⚠️⚠️ `alvoActivo` é **o próprio `alvo` ou `null`** — nunca um objecto novo —, e é isso que
    //    o torna seguro nesta lista. 📌 Quem chama guarda o alvo em **estado** (`Mapa.tsx`); um
    //    objecto construído a cada render daria um ciclo de pedidos ao servidor, e no navegador
    //    isso não se lê como defeito, lê-se como «o ecrã está lento» — foi o que aconteceu no
    //    `Substituir` com `indisponibilidade()` nas dependências de um efeito.
  }, [quem, periodo, desde, ate, accao, alvoActivo, comecarLeitura]);

  /**
   * ⚠️ Sem alvo **não há cadeia** — e a consulta nem se faz. «Repor até um momento» sem viagem
   * nomeada era sobre o plano inteiro, que é o instantâneo que o §5.2 do desenho recusa.
   */
  const carregarCadeia = useCallback(async () => {
    const aindaEAUltima = comecarCadeia();
    if (alvoActivo === null) {
      definirCadeia(new Map());
      return;
    }
    const params = paramsDoAlvo(alvoActivo);
    try {
      const r = await fetch(`/api/plano/como-estava?${params.toString()}`);
      const corpo: unknown = await r.json().catch(() => null);
      if (!aindaEAUltima()) return;
      // ⚠️ Um erro aqui **não** é um erro do diário: a lista continua a valer e a mostrar-se.
      //    O que se perde é o verbo «Como estava», e um mapa vazio é exactamente isso — nenhuma
      //    linha o oferece. 📌 Inventar um `reponivel: true` por omissão era o contrário.
      definirCadeia(
        r.ok && eCadeia(corpo) ? new Map(corpo.momentos.map((m) => [m.em, m])) : new Map(),
      );
    } catch {
      if (aindaEAUltima()) definirCadeia(new Map());
    }
  }, [alvoActivo, comecarCadeia]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    void carregarCadeia();
  }, [carregarCadeia]);

  const linhas = d?.linhas ?? [];
  const servidorIgnorouAccao = accao !== '' && linhas.some((l) => !l.accoes.includes(accao));
  /** Algum filtro além da omissão larga — o vazio diz «com estes filtros» e oferece a saída. */
  const filtrado = quem !== 'todos' || periodo !== 'tudo' || accao !== '';

  return (
    <section
      className="janela janela--diario"
      style={{ left: posicao.x, top: posicao.y }}
      aria-label="Diário"
    >
      <header
        className={`janela-cab faixa-quebra${aArrastar ? ' janela-cab--agarrada' : ''}`}
        onPointerDown={aoPressionar}
      >
          {/* ⚠️ O título NOMEIA a viagem quando há alvo. Duas janelas iguais chamadas «Diário»,
            uma filtrada e outra não, são indistinguíveis — e a filtrada é a que engana. */}
        <h2>{alvoActivo === null ? 'Diário' : `Diário · ${alvoActivo.rotulo}`}</h2>
        <BotaoActualizar actualizar={carregar} assinatura={() => JSON.stringify(d)} />
        <button type="button" className="btn btn--icone" onClick={fechar} aria-label="fechar">
          <IconeFechar />
        </button>
      </header>

      <div className="janela-corpo">
        {/* ⚠️⚠️ **O ÂMBITO DIZ-SE E LARGA-SE (P-30).** Um filtro que não se vê transforma «esta
            viagem não tem história» em «o plano não tem história», e as duas leem-se igual.
            📌 O alternador fica marcado por omissão — é a pergunta que se fez ao clicar — e
            desmarcá-lo mostra o diário inteiro sem fechar nada. */}
        {alvo !== undefined ? (
          <div className="diario-filtros faixa-quebra">
            <label className="diario-alternador">
              <input
                type="checkbox"
                checked={soEstaViagem}
                onChange={(e) => definirSoEstaViagem(e.target.checked)}
              />
              só esta viagem ({alvo.rotulo})
            </label>
          </div>
        ) : null}

        {/* ⚠️⚠️ **OS FILTROS NUMA SÓ LINHA, COM TRÊS RÓTULOS LEGÍVEIS** — 📗 cliente, 2026-09-30:
            *«está muito complexo e difícil de entender os filtros»*. Havia um botão «O que mudou
            desde ontem» numa linha e três selectores soltos noutra, com rótulos em minúsculas
            colados ao controlo («quemtoda a gente»), e o «O quê» mostrava a chave do código
            (`mudar executor`).
            📌 **O botão da pergunta de segunda-feira (§11 N) SAIU, e o que ele fazia fica:** era
            exactamente «Quem = toda a gente menos eu» + «Quando = desde ontem», as duas opções
            que continuam nos selectores — e o vazio dessa combinação mantém a frase própria
            («ninguém mexeu no plano desde ontem, além de si»). Contrapartida declarada: a
            pergunta passa a pedir dois cliques em vez de um; em troca, deixa de haver dois
            sítios a mandar no mesmo filtro. */}
        <div className="diario-filtros diario-filtros--linha">
          <label className="diario-filtro">
            <span className="diario-filtro-rotulo">Quem</span>
            <select
              value={quem}
              aria-label="Quem"
              onChange={(e) => definirQuem(e.target.value as QuemEscolhido)}
            >
              <option value="eu">eu</option>
              <option value="todos">toda a gente</option>
              {/* ⚠️ «menos eu» e não «outras pessoas»: o `sistema` também entra, e é uma
                  mudança que não foi minha. Ver `QuemNoDiario` em `apps/api/src/diario.ts`. */}
              <option value="outros">toda a gente menos eu</option>
              {/* 📗 «por utilizadores» — as pessoas que já apareceram nesta janela (ver
                  `pessoasVistas`). Alargar o «Quem» a toda a gente traz as restantes. */}
              {[...pessoasVistas].map(([id, nome]) => (
                <option key={id} value={`u:${id}`}>
                  {nome}
                </option>
              ))}
            </select>
          </label>
          <label className="diario-filtro">
            <span className="diario-filtro-rotulo">O quê</span>
            <select value={accao} aria-label="O quê" onChange={(e) => definirAccao(e.target.value)}>
              <option value="">tudo</option>
              {/* ⚠️ O nome vem do SERVIDOR (`NOME_DA_ACCAO`, a frase do diário sem os dados);
                  o valor continua a ser a chave, que é o que a rota filtra. */}
              {[...accoesVistas]
                .sort(([, a], [, b]) => a.localeCompare(b, 'pt'))
                .map(([a, nome]) => (
                  <option key={a} value={a}>
                    {nome}
                  </option>
                ))}
            </select>
          </label>
          <label className="diario-filtro">
            <span className="diario-filtro-rotulo">Quando</span>
            <select
              value={periodo}
              aria-label="Quando"
              onChange={(e) => definirPeriodo(e.target.value as PeriodoDoDiario)}
            >
              <option value="hoje">hoje</option>
              <option value="desde-ontem">desde ontem</option>
              <option value="7-dias">últimos 7 dias</option>
              <option value="30-dias">últimos 30 dias</option>
              <option value="tudo">sempre</option>
              <option value="entre">entre datas…</option>
            </select>
          </label>
          {periodo === 'entre' ? (
            <>
              <label className="diario-filtro">
                <span className="diario-filtro-rotulo">De</span>
                <input
                  type="date"
                  aria-label="desde"
                  value={desde}
                  onChange={(e) => definirDesde(e.target.value)}
                />
              </label>
              <label className="diario-filtro">
                <span className="diario-filtro-rotulo">Até</span>
                <input
                  type="date"
                  aria-label="até"
                  value={ate}
                  onChange={(e) => definirAte(e.target.value)}
                />
              </label>
            </>
          ) : null}
        </div>
        {/* ⚠️ Sem nenhuma data o servidor cai em «hoje» (um intervalo vazio não abre a lista
            inteira) — e diz-se, senão a lista de hoje lia-se como a resposta a «entre datas». */}
        {periodo === 'entre' && desde === '' && ate === '' ? (
          <p className="diario-aviso-filtro">escolha pelo menos uma data — até lá, mostra-se hoje.</p>
        ) : null}
        {/* ⚠️⚠️ **O filtro por acção só vale se o servidor o aplicar**, e a rota que o recebe vive
            em `servidor.ts`. Se chegar uma linha sem a acção pedida, a lista NÃO está filtrada —
            e diz-se, em vez de a mostrar como se estivesse (*configuração que não configura*). */}
        {servidorIgnorouAccao ? (
          <p className="diario-aviso-filtro">
            ⚠️ o servidor ainda não aplica o filtro por acção — a lista abaixo não está filtrada.
          </p>
        ) : null}

        {erro !== null ? <p className="desfazer-erro">{erro}</p> : null}

        {aCarregar && d === null ? <p className="diario-vazio">a carregar…</p> : null}

        {/* ⚠️⚠️ **UMA LISTA VAZIA É INFORMAÇÃO, E DIZ-SE — mas só se a pergunta estiver na
            frase.** «nada por aqui» sobre «o que mudou desde ontem» e sobre «os meus gestos de
            hoje» são a mesma frase para duas respostas diferentes, e a primeira é a que
            interessa a quem chega segunda-feira: *ninguém mexeu no plano*.
            ⚠️ E **todo o vazio tem saída** — um estado vazio sem saída é um beco, e esta casa
            já o pagou no «o que encaixa aqui». */}
        {!aCarregar && erro === null && linhas.length === 0 ? (
          <>
            <p className="diario-vazio">
              {alvoActivo !== null
                ? // ⚠️ Nomeia o alvo **e** a saída: um vazio que não diz porque é que está
                  // vazio manda procurar o defeito no sítio errado.
                  `nada registado nesta viagem${filtrado ? ' com estes filtros' : ''} — desmarque «só esta viagem» para ver o resto.`
                : desdeOntemSemMim
                  ? 'ninguém mexeu no plano desde ontem, além de si.'
                  : quem !== 'eu' || periodo !== 'hoje' || accao !== ''
                    ? 'nada por aqui — com «Quem», «O quê» e «Quando» como estão.'
                    : 'nada seu hoje — alargue o «Quem» ou o «Quando» para ver mais.'}
            </p>
            {alvoActivo === null && filtrado ? (
              <div className="desfazer-accoes faixa-quebra">
                <button
                  type="button"
                  className="btn btn--peq"
                  onClick={() => {
                    definirQuem('todos');
                    definirPeriodo('tudo');
                    definirAccao('');
                  }}
                >
                  Ver tudo, de toda a gente
                </button>
              </div>
            ) : null}
          </>
        ) : null}

        <ul className="diario-lista">
          {linhas.map((l) => (
            <li key={`${l.utilizadorId}|${l.gestoId ?? l.em}`} className="diario-linha">
              <div className="diario-linha-cabeca">
                <span className="diario-hora">{horaCurta(l.em)}</span>
                <span className={`diario-autor${l.sistema ? ' diario-autor--sistema' : ''}`}>
                  {l.autor}
                </span>
                {l.registos > 1 ? (
                  <span className="diario-contagem">{l.registos}×</span>
                ) : null}
              </div>
              <FraseDoDiario frase={l.frase} />
              <DiasDaLinha dias={l.dias ?? []} verNoMapa={verNoMapaEfectivo} nomeDoDia={nomeDoDiaEfectivo} />
              {l.aproximado ? (
                <p className="diario-aproximado">
                  ⚠️ agrupado por hora — este registo é anterior à marca de gesto
                </p>
              ) : null}
              {/* ⚠️⚠️ PEÇA 4 — a marca vem do servidor e já está na linha, antes de qualquer
                  clique. Ver o cabeçalho de `DesfazerDaLinha`. */}
              <DesfazerDaLinha
                linha={l}
                recarregar={() => {
                  void carregar();
                  void carregarCadeia();
                  recarregarMapa?.();
                }}
              />
              {/* ⚠️⚠️ PEÇA 5 — o segundo verbo da mesma linha, e só onde acrescenta alguma
                  coisa. Ver o cabeçalho de `ComoEstavaDaLinha`. */}
              {/* 2026-10-27 · montado com ou sem momento — ver o `momento` de `ComoEstavaDaLinha`. */}
              {alvoActivo !== null ? (
                <ComoEstavaDaLinha
                  linha={l}
                  momento={cadeia.get(l.em)}
                  alvo={alvoActivo}
                  recarregar={() => {
                    void carregar();
                    void carregarCadeia();
                    recarregarMapa?.();
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
